import { Link } from 'react-router-dom'

import { AdminFilters } from '#components/adminFilters/AdminFiltersPanel'
import { ReportDataWrapper } from '#components/ReportDataWrapper'
import { ReportFilterControls } from '#components/ReportFilterControls'
import { StatCard } from '#components/StatCard'
import { useFetch } from '#hooks/useAdminResource'
import { useTrackingFilters } from '#hooks/useTrackingFilters'
import { formatPercentage } from '#utils/format'

interface TrackingStats {
  totalTrackings: number
  byStatus: {
    status: string
    count: number
    percentage: number
  }[]
  carrierStatusBreakdown: {
    carrierCode: string
    total: number
    byStatus: {
      status: string
      count: number
    }[]
  }[]
  byAccount: {
    accountId: string
    accountName?: string
    count: number
    percentage: number
  }[]
  recentActivity: {
    date: string
    count: number
    byStatus: {
      status: string
      count: number
    }[]
  }[]
}

const getStatusCount = (
  items: { status: string; count: number }[],
  status: string,
) => items.find(s => s.status === status)?.count || 0

const getErrorCount = (items: { status: string; count: number }[]) =>
  getStatusCount(items, 'error') + getStatusCount(items, 'not-found')

const StatusColumns = ({
  byStatus,
}: {
  byStatus: { status: string; count: number }[]
}) => {
  const errorCount = getErrorCount(byStatus)

  return (
    <>
      <td className="text-right text-success">
        {getStatusCount(byStatus, 'delivered').toLocaleString()}
      </td>
      <td className="text-right text-primary">
        {getStatusCount(byStatus, 'in-transit').toLocaleString()}
      </td>
      <td className="text-right text-info">
        {getStatusCount(byStatus, 'out-for-delivery').toLocaleString()}
      </td>
      <td className="text-right">
        {getStatusCount(byStatus, 'pending').toLocaleString()}
      </td>
      <td className="text-right text-error">
        {errorCount > 0 ? errorCount.toLocaleString() : '-'}
      </td>
    </>
  )
}

const statusColumnHeaders = (
  <>
    <th className="text-right">Delivered</th>
    <th className="text-right">In Transit</th>
    <th className="text-right">Out for Delivery</th>
    <th className="text-right">Pending</th>
    <th className="text-right">Error/Not Found</th>
  </>
)

const getStatusColor = (status: string) => {
  switch (status) {
    case 'delivered':
      return 'text-success'
    case 'in-transit':
      return 'text-primary'
    case 'out-for-delivery':
      return 'text-info'
    case 'error':
    case 'not-found':
      return 'text-error'
    default:
      return 'text-base-content'
  }
}

const TrackingReporting = () => {
  const { localFilters, setLocalFilters, apiFilters } = useTrackingFilters()

  const {
    data: stats,
    isLoading,
    error,
    refetch: refresh,
  } = useFetch<TrackingStats>('trackings/report', apiFilters)

  return (
    <ReportDataWrapper
      isLoading={isLoading}
      error={error}
      loadingMessage="Loading tracking report..."
      onRetry={() => refresh()}
    >
      <div className="space-y-6">
        <AdminFilters isLoading={isLoading} onRefresh={refresh}>
          <ReportFilterControls
            filters={localFilters}
            onChange={setLocalFilters}
          />
        </AdminFilters>

        {stats && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-6 gap-4">
              <StatCard
                title="Total Trackings"
                value={stats.totalTrackings.toLocaleString()}
                color="text-primary"
              />
              <StatCard
                title="Delivered"
                value={getStatusCount(stats.byStatus, 'delivered')}
                color="text-success"
              />
              <StatCard
                title="Out for Delivery"
                value={getStatusCount(stats.byStatus, 'out-for-delivery')}
                color="text-info"
              />
              <StatCard
                title="In Transit"
                value={getStatusCount(stats.byStatus, 'in-transit')}
                color="text-info"
              />
              <StatCard
                title="Pending / No Data"
                value={getStatusCount(stats.byStatus, 'pending')}
                color="text-info"
              />
              <StatCard
                title="Errors"
                value={getErrorCount(stats.byStatus)}
                color="text-error"
              />
            </div>

            {/* Status Breakdown */}
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title">Tracking Status Breakdown</h2>
                <div className="overflow-x-auto">
                  <table className="table table-zebra">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th className="text-right">Count</th>
                        <th className="text-right">Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.byStatus.map(item => (
                        <tr key={item.status}>
                          <td>
                            <span
                              className={`font-medium ${getStatusColor(item.status)}`}
                            >
                              {item.status.toUpperCase().replace(/-/g, ' ')}
                            </span>
                          </td>
                          <td className="text-right">
                            {item.count.toLocaleString()}
                          </td>
                          <td className="text-right">
                            {item.percentage.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Top Carriers by Tracking Volume */}
            {stats.carrierStatusBreakdown?.length > 0 && (
              <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <h2 className="card-title">
                    Top Carriers by Tracking Volume
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="table table-zebra">
                      <thead>
                        <tr>
                          <th>Carrier Code</th>
                          <th className="text-right">Total</th>
                          <th className="text-right">%</th>
                          {statusColumnHeaders}
                        </tr>
                      </thead>
                      <tbody>
                        {stats.carrierStatusBreakdown.map(item => (
                          <tr key={item.carrierCode}>
                            <td className="font-mono font-medium uppercase">
                              {item.carrierCode}
                            </td>
                            <td className="text-right font-semibold">
                              {item.total.toLocaleString()}
                            </td>
                            <td className="text-right">
                              {formatPercentage(
                                item.total,
                                stats.totalTrackings,
                              )}
                              %
                            </td>
                            <StatusColumns byStatus={item.byStatus} />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Activity Timeline */}
            {stats.recentActivity.length > 0 && (
              <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <h2 className="card-title">Daily Activity by Status</h2>
                  <div className="overflow-x-auto">
                    <table className="table table-zebra">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th className="text-right">Total</th>
                          {statusColumnHeaders}
                        </tr>
                      </thead>
                      <tbody>
                        {stats.recentActivity.map(item => (
                          <tr key={item.date}>
                            <td>
                              <Link
                                to={`/tracking?createdAtFrom=${item.date}&createdAtTo=${item.date}`}
                                className="link link-primary hover:link-hover"
                              >
                                {item.date}
                              </Link>
                            </td>
                            <td className="text-right font-semibold">
                              {item.count.toLocaleString()}
                            </td>
                            <StatusColumns byStatus={item.byStatus} />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {!stats && !isLoading && !error && (
          <div className="alert">
            <span>No tracking data available for the selected date range.</span>
          </div>
        )}
      </div>
    </ReportDataWrapper>
  )
}

export { TrackingReporting }
